import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { PolicyholderFormFields } from '@app/app/esales/travel/_sections/TravelWizardSection/hooks/usePolicyholderFormFields';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { useTranslations } from 'next-intl';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { Heading } from '@app/app/_components/Heading/Heading';
import { useCallback, useState, ChangeEvent } from 'react';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { Button } from '@app/app/_components/Button/Button';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { PolicyholderInformationStep } from '@app/app/esales/travel/_sections/steps/PolicyholderInformationStep/PolicyholderInformationStep';
import { Address } from '@app/domain/model/Address';

interface Props {
    policyholderFields: PolicyholderFormFields;
    formState: FormState<PurchaseTravelInsuranceResult>;
    isCompany: boolean;
    personalCode: string;
    onSave: (data: PolicyholderFormFields) => void;
}

export function PolicyholderSummaryCard({ policyholderFields, formState, isCompany, personalCode, onSave }: Props) {
    const t = useTranslations();
    const [sideSheetOpen, setSideSheetOpen] = useState(false);
    const [editFormData, setEditFormData] = useState<PolicyholderFormFields>(policyholderFields);

    const handleFirstNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, firstName: e.target.value }));
    }, []);

    const handleLastNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, lastName: e.target.value }));
    }, []);

    const handleAddressChange = useCallback((address: Nullable<Address>) => {
        setEditFormData(prev => ({ ...prev, address }));
    }, []);

    const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, phoneNumber: e.target.value }));
    }, []);

    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, email: e.target.value }));
    }, []);

    const handleIsTravelerChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, isTraveler: e.target.checked }));
    }, []);

    const editHandlers = {
        handleFirstNameChange,
        handleLastNameChange,
        handleAddressChange,
        handlePhoneNumberChange,
        handleEmailChange,
        handleIsTravelerChange,
    };

    const handleOpenSideSheet = useCallback(() => {
        setEditFormData({
            ...policyholderFields,
            address: policyholderFields.address ? { ...policyholderFields.address } : null,
        });
        setSideSheetOpen(true);
    }, [policyholderFields]);

    const handleCloseSideSheet = useCallback(() => {
        setSideSheetOpen(false);
    }, []);

    const handleCancel = useCallback(() => {
        handleCloseSideSheet();
    }, [handleCloseSideSheet]);

    const handleSave = useCallback(() => {
        onSave(editFormData);
        handleCloseSideSheet();
    }, [editFormData, onSave, handleCloseSideSheet]);

    const sideSheetHeader = (
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <div className="flex items-center gap-3">
                <button className="cursor-pointer" onClick={handleCloseSideSheet}>
                    <ArrowLeftIcon className="h-5 w-5 text-neutral-500" />
                </button>
                <Heading level="h3">{t('eshop.travel.editPolicyholderInformation')}</Heading>
            </div>
        </div>
    );

    const sideSheetFooter = (
        <div className="flex h-min w-full items-center justify-between gap-6 bg-white px-6 py-4 shadow-2xl">
            <Button
                variant="outlined"
                className="w-full"
                rightAddon={<CloseIcon className="size-4" />}
                onClick={handleCancel}>
                {t('general.cancel')}
            </Button>
            <Button className="w-full" rightAddon={<CheckThinIcon className="size-5" />} onClick={handleSave}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <>
            <CollapsablePanel
                label={
                    isCompany
                        ? policyholderFields.firstName
                        : `${policyholderFields.firstName} ${policyholderFields.lastName}`
                }
                collapsable={false}
                addon={<TextButton onClick={handleOpenSideSheet}>{t('general.edit')}</TextButton>}>
                <div className="p-6">
                    <div className="flex flex-col gap-2 border-b border-neutral-150 pb-4">
                        <Heading level="h4">
                            {isCompany
                                ? t('profile.companyInformation')
                                : t('eshop.travel.summaryStep.personalInformation')}
                        </Heading>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            <ReadOnlyField
                                label={isCompany ? t('profile.companyNameLabel') : t('eshop.travel.summaryStep.name')}>
                                {policyholderFields.firstName}
                            </ReadOnlyField>
                            {!isCompany && (
                                <ReadOnlyField label={t('eshop.travel.summaryStep.surname')}>
                                    {policyholderFields.lastName}
                                </ReadOnlyField>
                            )}
                            <ReadOnlyField
                                label={
                                    isCompany
                                        ? t('profile.companyCodeLabel')
                                        : t('eshop.travel.summaryStep.personalCode')
                                }>
                                {personalCode}
                            </ReadOnlyField>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 border-b border-neutral-150 py-4">
                        <Heading level="h4">{t('eshop.travel.summaryStep.contactInformation')}</Heading>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <ReadOnlyField className="sm:col-span-2" label={t('eshop.travel.summaryStep.addressLabel')}>
                                {policyholderFields.address
                                    ? `${policyholderFields.address.street} ${policyholderFields.address.houseNumber}${policyholderFields.address.apartmentNumber ? `-${policyholderFields.address.apartmentNumber}` : ''}, ${policyholderFields.address.city}, ${policyholderFields.address.postalCode}`
                                    : ''}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.travel.summaryStep.phone')}>
                                {policyholderFields.phoneNumber ? `+370${policyholderFields.phoneNumber}` : ''}
                            </ReadOnlyField>
                            <ReadOnlyField
                                className="max-sm:break-words sm:text-nowrap"
                                label={t('eshop.travel.summaryStep.email')}>
                                {policyholderFields.email}
                            </ReadOnlyField>
                        </div>
                    </div>
                    {!isCompany && (
                        <div className="flex flex-col gap-2 pt-4">
                            <Heading level="h4">{t('eshop.travel.summaryStep.insurerOfTravelPolicy')}</Heading>
                            <ReadOnlyField label={t('eshop.travel.summaryStep.insurerOfPolicy')}>
                                {policyholderFields.isTraveler
                                    ? t('eshop.travel.summaryStep.insuredIsTraveler')
                                    : t('eshop.travel.summaryStep.insuredIsNotTraveler')}
                            </ReadOnlyField>
                        </div>
                    )}
                </div>
            </CollapsablePanel>

            <SideSheet open={sideSheetOpen} header={sideSheetHeader} footer={sideSheetFooter} contentClassName="p-6">
                {sideSheetOpen && (
                    <div className="flex flex-col gap-6">
                        <PolicyholderInformationStep
                            policyholderFields={editFormData}
                            policyholderHandlers={editHandlers}
                            formState={formState}
                            isCompany={isCompany}
                            personalCode={personalCode}
                            disabledCheckbox={true}
                        />
                    </div>
                )}
            </SideSheet>
        </>
    );
}
